import { useEffect, useState } from 'react';
import {
  EVENTO_PERSONA_CAMBIATA,
  destinazioneDopoCambioPersona,
  personaCambiataInAltraScheda,
  sessioneAssociatoNonInUso,
} from '../utils/sezioniDipendente';

export { EVENTO_PERSONA_CAMBIATA };

const CHIAVI = ['associate_token', 'associate_data'];

/** Porta la scheda a `destinazione` (l'area riservata va all'accesso associati: socket e stato React spariscono). */
export function navigaDopoCambioPersona(destinazione: string): void {
  window.location.assign(destinazione);
}

const tokenSalvato = (): string | null => {
  try {
    return window.localStorage.getItem('associate_token');
  } catch (e) {
    return null;
  }
};

/** Annuncia alla scheda il cambio di persona (Header, menu mobile, diretta e chat rileggono la sessione). */
const annunciaCambioPersona = () => {
  try {
    window.dispatchEvent(new CustomEvent(EVENTO_PERSONA_CAMBIATA));
  } catch (e) {
    // un ascoltatore guasto non ferma la gestione del cambio
  }
};

/**
 * Browser condiviso con più schede (dipendenti dello studio, revisione Fable 23/10/2026): se in un'ALTRA scheda
 * esce la persona o ne entra un'altra (titolare ↔ dipendente, dipendente ↔ dipendente), questa scheda resterebbe
 * con i socket `/notifications` e `/chat`, la campanella e la chat della persona di prima. Ascolta `storage` su
 * `associate_token`/`associate_data` (e lo svuotamento del localStorage) e, al cambio di persona, annuncia
 * `EVENTO_PERSONA_CAMBIATA`; nell'area riservata porta poi la scheda all'accesso associati (e smette di ascoltare).
 * Sulle pagine pubbliche NON ricarica: un modulo in compilazione (wizard Rete Certificata, «Diventa socio»,
 * contatti, modifica domanda) andrebbe perso, e un accesso fatto sopra un'altra persona passa sempre da un logout
 * (`AssociateLoginPage.chiudiSessionePrecedente`). Lì basta l'evento: Header e menu mobile rimontano la campanella
 * (`useGenerazionePersona`, il socket `/notifications` della persona di prima si chiude) e si continua ad ascoltare.
 * Il token rinnovato della stessa persona non tocca nulla.
 * Nessuna azione nelle pagine che non usano la sessione dell'associato (`/admin`, `/area-associativa`,
 * `sessioneAssociatoNonInUso`): «Accedi come associato» scrive `associate_token` dal pannello admin e le altre schede
 * admin non devono perdere il lavoro. Si continua ad ascoltare: se la scheda passa poi al sito, vale il percorso del
 * momento dell'evento.
 * `vai` sostituibile nei test.
 */
export function useCambioPersonaAltraScheda(vai: (destinazione: string) => void = navigaDopoCambioPersona): void {
  useEffect(() => {
    // Token visto da questa scheda: serve solo per `localStorage.clear()` (chiave null, senza i valori di prima)
    let tokenVisto = tokenSalvato();
    const gestore = (e: StorageEvent) => {
      if (e.storageArea && e.storageArea !== window.localStorage) return;
      if (e.key !== null && !CHIAVI.includes(e.key)) return;
      // Fuori dall'area riservata un logout può dare due annunci (token e poi dati): innocuo, ognuno rilegge la sessione
      const cambiata = e.key === null
        ? !!tokenVisto && !tokenSalvato()
        : personaCambiataInAltraScheda(e.key, e.oldValue, e.newValue);
      if (e.key === null || e.key === 'associate_token') tokenVisto = tokenSalvato();
      if (!cambiata || sessioneAssociatoNonInUso(window.location.pathname)) return;
      const destinazione = destinazioneDopoCambioPersona(window.location.pathname);
      if (destinazione) window.removeEventListener('storage', gestore);
      annunciaCambioPersona();
      if (destinazione) vai(destinazione);
    };
    window.addEventListener('storage', gestore);
    return () => window.removeEventListener('storage', gestore);
  }, [vai]);
}

/**
 * Numero che cresce a ogni `EVENTO_PERSONA_CAMBIATA`: chi lo usa si ridisegna (e rilegge la sessione dal
 * localStorage) e lo mette nella `key` di ciò che va rimontato con la persona nuova, anche quando l'id persona
 * resta lo stesso (es. «Accedi come associato» sullo stesso studio).
 */
export function useGenerazionePersona(): number {
  const [generazione, setGenerazione] = useState(0);
  useEffect(() => {
    const cambio = () => setGenerazione((g) => g + 1);
    window.addEventListener(EVENTO_PERSONA_CAMBIATA, cambio);
    return () => window.removeEventListener(EVENTO_PERSONA_CAMBIATA, cambio);
  }, []);
  return generazione;
}

/**
 * Valore letto dalla sessione (es. il token della diretta) e riletto solo a ogni `EVENTO_PERSONA_CAMBIATA`: sulle
 * pagine pubbliche, che non si ricaricano più al cambio di persona, non resta quello della persona di prima.
 * `leggi` va passata stabile (funzione di modulo o memorizzata).
 */
export function useLetturaPerPersona<T>(leggi: () => T): T {
  const [valore, setValore] = useState<T>(leggi);
  useEffect(() => {
    const cambio = () => setValore(() => leggi());
    window.addEventListener(EVENTO_PERSONA_CAMBIATA, cambio);
    return () => window.removeEventListener(EVENTO_PERSONA_CAMBIATA, cambio);
  }, [leggi]);
  return valore;
}

export default useCambioPersonaAltraScheda;
